/**
 * Viewport-clamped placement for a fixed panel hanging off an anchor rect.
 *
 * Extracted from `usePopoverPosition` so the guided-tour bubble clamps with exactly the same
 * arithmetic as every portalled picker, rather than re-deriving it.
 */

export const VIEWPORT_MARGIN = 8;

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

export interface PanelPlacementInput {
  anchor: AnchorRect;
  panelWidth: number;
  panelHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  /** Distance between the anchor and the panel. */
  gap?: number;
  /**
   * Which anchor edge the panel lines up with: `start` (its left edge on the anchor's left, the default) or
   * `end` (its right edge on the anchor's right, for a trigger at the right of a toolbar). Clamped either way.
   */
  align?: 'start' | 'end';
}

export interface PanelPosition {
  top: number;
  left: number;
}

/**
 * Below the anchor when it fits or is the roomier side, otherwise above. Always clamped so the
 * panel stays on screen even when neither side has room.
 */
export const computeAnchoredPosition = ({
  anchor,
  panelWidth,
  panelHeight,
  viewportWidth,
  viewportHeight,
  gap = 4,
  align = 'start',
}: PanelPlacementInput): PanelPosition => {
  const spaceBelow = viewportHeight - anchor.bottom - VIEWPORT_MARGIN;
  const spaceAbove = anchor.top - VIEWPORT_MARGIN;

  let top: number;
  if (spaceBelow >= panelHeight || spaceBelow >= spaceAbove) {
    // Below the trigger, but clamped so the bottom edge stays on screen even when neither
    // side has room. A picker whose confirm button is off the viewport is unusable.
    top = Math.min(
      anchor.bottom + gap,
      Math.max(VIEWPORT_MARGIN, viewportHeight - panelHeight - VIEWPORT_MARGIN)
    );
  } else {
    // Flip above when that is genuinely the roomier side.
    top = Math.max(VIEWPORT_MARGIN, anchor.top - panelHeight - gap);
  }

  const wanted = align === 'end' ? anchor.right - panelWidth : anchor.left;
  const left = Math.min(
    Math.max(VIEWPORT_MARGIN, wanted),
    Math.max(VIEWPORT_MARGIN, viewportWidth - panelWidth - VIEWPORT_MARGIN)
  );

  return { top, left };
};

/**
 * The tallest a panel may be and still fit beside its anchor: the roomier of the space below and above it, less
 * the gap and the viewport margin. Depends only on the anchor, never on where the panel was put, so a panel
 * capped with it cannot feed back into its own placement. Never below `floor` unless the viewport itself is.
 */
export const availablePanelHeight = (
  anchor: Pick<AnchorRect, 'top' | 'bottom'>,
  viewportHeight: number,
  { gap = 4, floor = 160 }: { gap?: number; floor?: number } = {}
): number => {
  const below = viewportHeight - anchor.bottom - gap - VIEWPORT_MARGIN;
  const above = anchor.top - gap - VIEWPORT_MARGIN;
  const whole = Math.max(0, viewportHeight - 2 * VIEWPORT_MARGIN);
  return Math.min(whole, Math.max(floor, below, above));
};
